import type { IFeedbackRepository } from "@/core/repositories/interfaces/IFeedbackRepository";
import type {
  Feedback,
  ListFeedbacksInput,
  ListFeedbacksResult,
  MarkFeedbackSeenResult,
} from "@/core/services/types/feedback.types";
import { createClient } from "@/lib/supabase";

function isFeedback(value: unknown): value is Feedback {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && typeof row.message === "string";
}

export class FeedbackRepository implements IFeedbackRepository {
  async list(
    input: ListFeedbacksInput = {},
  ): Promise<ListFeedbacksResult> {
    const supabase = await createClient();
    const { data, error } = await supabase.functions.invoke(
      "manage-firebase-feedbacks",
      {
        body: {
          action: "list",
          pageSize: input.pageSize,
          pageToken: input.pageToken ?? undefined,
        },
      },
    );

    if (error) {
      console.error("[FeedbackRepository.list]", error.message);
      return { feedbacks: [], nextPageToken: null };
    }

    const payload = data as {
      success?: boolean;
      error?: string;
      data?: {
        feedbacks?: unknown[];
        nextPageToken?: string | null;
      };
    };

    if (!payload?.success || !payload.data) {
      console.error(
        "[FeedbackRepository.list]",
        payload?.error ?? "unknown error",
      );
      return { feedbacks: [], nextPageToken: null };
    }

    const feedbacks = (payload.data.feedbacks ?? []).filter(isFeedback);
    return {
      feedbacks,
      nextPageToken: payload.data.nextPageToken ?? null,
    };
  }

  async markSeen(id: string): Promise<MarkFeedbackSeenResult> {
    const supabase = await createClient();
    const { data, error } = await supabase.functions.invoke(
      "manage-firebase-feedbacks",
      {
        body: {
          action: "markSeen",
          id,
        },
      },
    );

    if (error) {
      console.error("[FeedbackRepository.markSeen]", error.message);
      return { success: false, error: error.message };
    }

    const payload = data as {
      success?: boolean;
      error?: string;
      data?: { id: string; seen: boolean };
    };

    if (!payload?.success || !payload.data) {
      const message = payload?.error ?? "unknown error";
      console.error("[FeedbackRepository.markSeen]", message);
      return { success: false, error: message };
    }

    return { success: true, data: payload.data };
  }
}
